'use client';

import { useId } from '@react-aria/utils';
import { useControlledState } from '@react-stately/utils';
import clsx from 'clsx';
import { createContext, useContext, type ReactNode } from 'react';
import { Button as RACButton, Link as RACLink } from 'react-aria-components';
import { IconChevronDown } from '../../icons/index.js';
import type { Href, NavigationProps, StringLikeChildren } from '../types.js';
import { Text } from '../typography/text.js';

export interface UNSTABLE_NavListProps {
  children: ReactNode;
}

/**
 * Displays a vertical navigation list.
 */
export function UNSTABLE_NavList({ children }: UNSTABLE_NavListProps) {
  return <ul className="cim-nav-list">{children}</ul>;
}

const NavListRowContext = createContext<{ depth: number } | null>(null);

function NavListRow({ children }: { children: ReactNode }) {
  const navListRowContext = useContext(NavListRowContext);
  const newDepth = navListRowContext == null ? 0 : navListRowContext.depth + 1;

  return (
    <NavListRowContext.Provider value={{ depth: newDepth }}>
      <li className="cim-nav-list-row" style={{ '--cim-nav-list-depth': newDepth } as React.CSSProperties}>
        {children}
      </li>
    </NavListRowContext.Provider>
  );
}

export interface UNSTABLE_NavListItemProps extends NavigationProps {
  children: StringLikeChildren;
  icon?: ReactNode;
  href?: Href;
  isCurrent?: boolean;
  isDisabled?: boolean;
}

/**
 * Displays a single navigation item.
 */
export function UNSTABLE_NavListItem({
  children,
  icon,
  href,
  isCurrent,
  isDisabled,
  routerOptions,
  ...props
}: UNSTABLE_NavListItemProps) {
  return (
    <NavListRow>
      <RACLink
        {...props}
        className="cim-nav-list-item"
        href={href}
        routerOptions={routerOptions}
        aria-current={isCurrent ? 'page' : undefined}
        isDisabled={isDisabled}
        data-active={isCurrent || undefined}
      >
        {icon}
        <Text as="span" variant="medium" UNSAFE_className="cim-nav-list-item-label">
          {children}
        </Text>
      </RACLink>
    </NavListRow>
  );
}

export interface UNSTABLE_NavListGroupProps {
  children: ReactNode;
  label: StringLikeChildren;
  icon?: ReactNode;
  isExpanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
  isDisabled?: boolean;
}

/**
 * Displays an expandable navigation item group.
 */
export function UNSTABLE_NavListGroup({
  children,
  label,
  icon,
  isExpanded,
  defaultExpanded = false,
  onExpandedChange,
  isDisabled,
}: UNSTABLE_NavListGroupProps) {
  const groupId = useId();
  const [expanded, setExpanded] = useControlledState(isExpanded, defaultExpanded, onExpandedChange);

  return (
    <NavListRow>
      <RACButton
        className="cim-nav-list-item"
        onPress={() => setExpanded(!expanded)}
        aria-controls={groupId}
        aria-expanded={expanded}
        isDisabled={isDisabled}
      >
        {icon}
        <Text as="span" variant="medium" UNSAFE_className="cim-nav-list-item-label">
          {label}
        </Text>
        <IconChevronDown
          UNSAFE_className={clsx('cim-nav-list-group-icon', expanded && 'cim-nav-list-group-icon-expanded')}
        />
      </RACButton>
      <ul id={groupId} className="cim-nav-list-group-content" hidden={!expanded}>
        {children}
      </ul>
    </NavListRow>
  );
}
